import { createRoot } from 'react-dom/client'
import posthog from 'posthog-js'
import * as Sentry from "@sentry/react"
import { Replay } from "@sentry/replay"
import App from './App.tsx'
import './index.css'
import React from 'react'

// Initialize PostHog
posthog.init('phc_lllCKuFHT9DuyfWCql2CNTWnk9JEPrXqodwng9qxvbz', {
    api_host: 'https://i.evalops.dev',
    person_profiles: 'identified_only'
})

// Initialize feature flags for development only
if (process.env.NODE_ENV === 'development') {
  import('./dev/featureFlags').then(({ bootstrapFeatureFlags }) => {
    bootstrapFeatureFlags();
  });
}

// Initialize Sentry with enhanced error tracking
Sentry.init({
  dsn: "https://94479b32be52867506c8c397479eb62b@o4509296843358208.ingest.us.sentry.io/4509302763094016",
  environment: import.meta.env.PROD ? 'production' : 'development',
  sendDefaultPii: true,
  integrations: [
    Sentry.replayIntegration(),
    Sentry.browserProfilingIntegration(),
  ],
  // Enhanced error tracking
  tracesSampleRate: 1.0, // Capture 100% of transactions for debugging
  replaysSessionSampleRate: 1.0, // Capture ALL sessions during debugging
  replaysOnErrorSampleRate: 1.0, // Capture ALL error replays
  
  // Custom error filtering and enhancement
  beforeSend(event, hint) {
    // Enhanced React error handling - catch all React errors, not just minified ones
    if (event.exception?.values?.[0]?.type === 'Error') {
      const errorMessage = hint.originalException?.message || event.exception.values[0].value || '';
      
      // Detect React Error #300 (all variants)
      if (errorMessage.includes('Minified React error #300') || 
          errorMessage.includes('Maximum update depth exceeded') ||
          errorMessage.includes('rendered fewer hooks than expected') ||
          errorMessage.includes('Rendered fewer hooks than expected') ||
          errorMessage.includes('accidental early return statement')) {
        
        // Add authentication state context
        const authState = {
          url: window.location.href,
          pathname: window.location.pathname,
          search: window.location.search,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          localStorage_keys: Object.keys(localStorage),
          sessionStorage_keys: Object.keys(sessionStorage),
          error_message_full: errorMessage,
          is_minified: errorMessage.includes('Minified React error')
        };
        
        // Add localStorage data (non-sensitive)
        try {
          authState.cached_user = localStorage.getItem('cached_user') ? 'present' : 'absent';
          authState.cached_organization = localStorage.getItem('cached_organization') ? 'present' : 'absent';
        } catch (e) {
          authState.localStorage_error = e.message;
        }
        
        event.contexts = {
          ...event.contexts,
          authentication_state: authState
        };
        
        event.tags = {
          ...event.tags,
          error_type: 'react_infinite_loop',
          react_error: '300',
          critical: 'true',
          error_variant: errorMessage.includes('Maximum update depth') ? 'infinite_update' :
                        (errorMessage.includes('rendered fewer hooks') || errorMessage.includes('Rendered fewer hooks')) ? 'hook_rule_violation' :
                        errorMessage.includes('accidental early return') ? 'early_return' : 'unknown'
        };
        
        event.level = 'fatal';
        
        console.error('[SENTRY] React Error #300 variant detected:', {
          type: event.tags.error_variant,
          message: errorMessage,
          context: authState
        });
      }
      
      // Also catch any component lifecycle errors
      if (errorMessage.includes('Component') || errorMessage.includes('useEffect') || errorMessage.includes('useState')) {
        event.tags = {
          ...event.tags,
          error_type: 'react_lifecycle',
          component_error: 'true'
        };
        
        console.error('[SENTRY] React lifecycle error detected:', errorMessage);
      }
    }
    
    return event;
  },
  
  // Capture unhandled promise rejections
  captureUnhandledRejections: true,
  
  // Debug mode for development
  debug: !import.meta.env.PROD
});

// Global error handlers for additional debugging
window.addEventListener('error', (event) => {
  console.error('[GLOBAL ERROR]', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error,
    stack: event.error?.stack,
    url: window.location.href,
    timestamp: new Date().toISOString()
  });
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('[UNHANDLED REJECTION]', {
    reason: event.reason,
    promise: event.promise,
    url: window.location.href,
    timestamp: new Date().toISOString()
  });
});

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
// Force deployment - Sun Jun  1 14:54:28 PDT 2025
